if (!customElements.get('gift-bundle-builder')) {
  class GiftBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
      this.drops = Array.from(this.querySelectorAll('[data-drop]'));
      this.available = this.drops.filter((drop) => !drop.disabled);
      this.slots = this.querySelector('[data-slots]');
      this.statusEl = this.querySelector('[data-status]');
      this.compareEl = this.querySelector('[data-compare]');
      this.totalEl = this.querySelector('[data-total]');
      this.saveEl = this.querySelector('[data-save]');
      this.addButton = this.querySelector('[data-add]');
      this.messageInput = this.querySelector('[data-gift-message]');
      this.errorEl = this.querySelector('[data-error]');
      this.selected = [];
      this.notice = '';
      this.loading = false;
      this.isAll = false;

      const prices = this.available.map((drop) => Number(drop.dataset.price));
      this.uniformPrice = prices.every((price) => price === prices[0]);

      this.tiers.forEach((tier) => tier.addEventListener('click', () => this.setTier(tier)));
      this.drops.forEach((drop) => drop.addEventListener('click', () => this.toggleDrop(drop)));
      this.addButton.addEventListener('click', () => this.addToCart());

      const activeTier = this.tiers.find((tier) => tier.getAttribute('aria-pressed') === 'true') || this.tiers[0];
      this.setTier(activeTier);
    }

    setTier(tier) {
      const wasAll = this.isAll;
      this.isAll = tier.dataset.size === 'all';
      this.size = this.isAll ? this.available.length : Number(tier.dataset.size);
      this.discount = Number(tier.dataset.discount);
      this.discountCode = tier.dataset.code || '';
      this.bundleLabel = this.isAll ? 'Full collection' : `${this.size}-drop bundle`;
      this.tiers.forEach((t) => t.setAttribute('aria-pressed', String(t === tier)));

      if (this.isAll) {
        this.selected = [...this.available];
      } else if (wasAll) {
        this.selected = [];
      } else {
        // Switching from 6 down to 3 keeps the first picks
        this.selected = this.selected.slice(0, this.size);
      }
      this.notice = '';
      this.render();
    }

    toggleDrop(drop) {
      if (this.isAll) {
        this.notice = 'Every drop is included in the full collection.';
        this.render();
        return;
      }

      const index = this.selected.indexOf(drop);
      if (index > -1) {
        this.selected.splice(index, 1);
        this.notice = '';
      } else if (this.selected.length < this.size) {
        this.selected.push(drop);
        this.notice = '';
      } else {
        this.notice = 'Your bundle is full. Tap a selected drop to swap it out.';
      }
      this.render();
    }

    // Price of the picks so far, topped up with the cheapest drops for any empty spots
    estimateTotal() {
      const remaining = this.size - this.selected.length;
      const price = (drop) => Number(drop.dataset.price);
      const fillers = this.available
        .filter((drop) => !this.selected.includes(drop))
        .map(price)
        .sort((a, b) => a - b)
        .slice(0, remaining);
      return this.selected.map(price).concat(fillers).reduce((sum, value) => sum + value, 0);
    }

    render() {
      const count = this.selected.length;
      const remaining = this.size - count;

      this.classList.toggle('is-all', this.isAll);
      this.drops.forEach((drop) => {
        const isSelected = this.selected.includes(drop);
        drop.setAttribute('aria-pressed', String(isSelected));
        drop.classList.toggle('is-dimmed', !isSelected && remaining === 0 && !this.isAll);
      });

      if (this.slots) {
        this.slots.replaceChildren(
          ...Array.from({ length: this.size }, (_, i) => {
            const slot = document.createElement('span');
            slot.className = 'gift-bundle__slot';
            const drop = this.selected[i];
            if (drop) {
              slot.classList.add('is-filled');
              if (drop.dataset.thumb) {
                const img = document.createElement('img');
                img.src = drop.dataset.thumb;
                img.alt = '';
                slot.append(img);
              }
            }
            return slot;
          })
        );
      }

      const total = this.estimateTotal();
      const discounted = Math.round((total * (100 - this.discount)) / 100);
      const prefix = remaining > 0 && !this.uniformPrice ? 'From ' : '';
      if (this.compareEl) this.compareEl.textContent = this.size && this.discount ? this.formatMoney(total) : '';
      if (this.totalEl) this.totalEl.textContent = this.size ? prefix + this.formatMoney(discounted) : '';
      if (this.saveEl) this.saveEl.textContent = this.discount ? `Save ${this.discount}%` : '';

      if (this.notice) {
        this.statusEl.textContent = this.notice;
      } else if (this.isAll) {
        this.statusEl.textContent = `All ${this.size} drops included · ${this.discount}% off applied in your cart`;
      } else if (remaining > 0) {
        this.statusEl.textContent = `${count} of ${this.size} selected · pick ${remaining} more to save ${this.discount}%`;
      } else {
        this.statusEl.textContent = `Bundle complete · ${this.discount}% off applied in your cart`;
      }

      if (!this.loading) {
        this.addButton.disabled = remaining !== 0 || this.size === 0;
        if (this.isAll) {
          this.addButton.textContent = `Add all ${this.size} drops to cart`;
        } else {
          this.addButton.textContent =
            remaining > 0 ? `Pick ${remaining} more drop${remaining === 1 ? '' : 's'}` : `Add ${this.size} drops to cart`;
        }
      }
    }

    setLoading(loading) {
      this.loading = loading;
      this.addButton.setAttribute('aria-busy', String(loading));
      if (loading) {
        this.addButton.disabled = true;
        this.addButton.textContent = 'Adding…';
      } else {
        this.render();
      }
    }

    async addToCart() {
      if (this.loading || !this.size || this.selected.length !== this.size) return;
      this.setLoading(true);
      this.errorEl.textContent = '';

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cartDrawer = document.querySelector('cart-drawer');
      const canUseDrawer =
        cartDrawer &&
        typeof cartDrawer.renderContents === 'function' &&
        typeof cartDrawer.getSectionsToRender === 'function';

      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      const addBody = {
        items: this.selected.map((drop) => ({
          id: Number(drop.dataset.variantId),
          quantity: 1,
          properties: { 'Gift bundle': this.bundleLabel },
        })),
      };

      // Applying the code replaces any code already on the cart, which is what we want here
      const updateBody = {};
      if (this.discountCode) updateBody.discount = this.discountCode;
      const message = this.messageInput ? this.messageInput.value.trim() : '';
      if (message) updateBody.attributes = { 'Gift message': message };
      if (canUseDrawer) {
        updateBody.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        updateBody.sections_url = window.location.pathname;
      }

      try {
        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify(addBody),
        });
        const addData = await addResponse.json();
        if (!addResponse.ok) {
          throw new Error(addData.description || addData.message || 'Something went wrong. Please try again.');
        }

        // Re-render the drawer from this response so it shows the discount
        const updateResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify(updateBody),
        });
        const updateData = updateResponse.ok ? await updateResponse.json() : null;

        if (canUseDrawer && updateData && updateData.sections) {
          try {
            // The theme hides the drawer contents while this class is set, and its own add-to-cart clears it too
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(updateData);
            this.setLoading(false);
            return;
          } catch (error) {
            // Drawer didn't accept the response, fall through to the cart page
          }
        }

        // The /discount/ link applies the code as a backup if the cart update didn't
        window.location.href = this.discountCode
          ? `${root}discount/${encodeURIComponent(this.discountCode)}?redirect=${encodeURIComponent(`${root}cart`)}`
          : `${root}cart`;
      } catch (error) {
        this.errorEl.textContent = error.message;
        this.setLoading(false);
      }
    }

    formatMoney(cents) {
      const value = cents / 100;
      const format = (decimals, thousands = ',', decimal = '.') => {
        const [whole, fraction] = value.toFixed(decimals).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
      };
      const formats = {
        amount: () => format(2),
        amount_no_decimals: () => format(0),
        amount_with_comma_separator: () => format(2, '.', ','),
        amount_no_decimals_with_comma_separator: () => format(0, '.', ','),
        amount_with_apostrophe_separator: () => format(2, "'", '.'),
      };
      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (formats[key] || formats.amount)());
    }
  }

  customElements.define('gift-bundle-builder', GiftBundleBuilder);
}

if (!customElements.get('gift-product-gallery')) {
  class GiftProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.show(index)));
    }

    show(index) {
      this.slides.forEach((slide, i) => (slide.hidden = i !== index));
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    }
  }

  customElements.define('gift-product-gallery', GiftProductGallery);
}
